import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, ShoppingCart } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type ShopifyProduct } from '@/lib/shopify';
import { useShopifyCartStore } from '@/stores/cartStore';
import { useShopifyWishlist } from '@/contexts/ShopifyWishlistContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformStatus } from '@/contexts/PlatformStatusContext';
import { toast } from 'sonner';
import { getHoodiePair } from '@/lib/hoodiePairs';




interface ShopifyProductCardProps {
  product: ShopifyProduct;
  className?: string;
  allProducts?: ShopifyProduct[];
}

export const ShopifyProductCard = ({ product, className, allProducts }: ShopifyProductCardProps) => {
  const addItem = useShopifyCartStore((state) => state.addItem);
  const isLoading = useShopifyCartStore((state) => state.isLoading);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useShopifyWishlist();
  const { user } = useAuth();
  const { purchasesDisabled } = usePlatformStatus();
  const navigate = useNavigate();
  const [isHeartAnimating, setIsHeartAnimating] = useState(false);

  const pair = useMemo(
    () => (allProducts ? getHoodiePair(product, allProducts) : null),
    [product, allProducts]
  );

  const [variantKey, setVariantKey] = useState<'standard' | 'zip'>(() => {
    if (!pair) return 'standard';
    return pair.zip.node.handle === product.node.handle ? 'zip' : 'standard';
  });

  const active: ShopifyProduct = pair ? (variantKey === 'zip' ? pair.zip : pair.standard) : product;
  const { node } = active;
  const inWishlist = isInWishlist(node.id);

  const price = parseFloat(node.priceRange.minVariantPrice.amount);
  const compareAtPrice = node.compareAtPriceRange?.minVariantPrice?.amount ?
  parseFloat(node.compareAtPriceRange.minVariantPrice.amount) :
  null;
  const hasDiscount = !!compareAtPrice && compareAtPrice > price;
  const imageUrl = node.images.edges[0]?.node.url;
  const firstVariant = node.variants.edges[0]?.node;

  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info('Please log in to save to wishlist');
      navigate('/auth', { state: { from: window.location.pathname } });
      return;
    }
    setIsHeartAnimating(true);
    if (inWishlist) {
      await removeFromWishlist(node.id);
      toast.success('Removed from wishlist');
    } else {
      await addToWishlist(node.id, node.handle);
      toast.success('Added to wishlist');
    }
    setTimeout(() => setIsHeartAnimating(false), 300);
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (purchasesDisabled) {
      toast.error('Purchases are temporarily paused. Please try again later.');
      return;
    }

    if (!firstVariant) return;

    if (!node.availableForSale) {
      toast.error('This product is sold out');
      return;
    }

    const variant = firstVariant;
    const selectedOptions = variant.selectedOptions || [];

    const success = await addItem({
      product: active,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions
    });

    if (success) {
      toast.success(`${node.title} added to cart`);
    } else {
      toast.error(`Failed to add ${node.title} to cart`);
    }
  };

  return (
    <div className={cn('card-racing group flex flex-col h-full', className)}>
      <Link to={`/product/${node.handle}`} className="block">
        <div className="aspect-[3/4] bg-black relative overflow-hidden">
          {imageUrl ?
          <img
            src={imageUrl}
            alt={node.images.edges[0]?.node.altText || node.title}
            loading="lazy"
            className="w-full h-full object-center group-hover:scale-105 transition-transform duration-500 object-contain" /> :


          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              <ShoppingBag className="w-12 h-12" />
            </div>
          }
          {!node.availableForSale &&
          <span className="absolute top-3 left-3 px-2 py-1 bg-muted text-muted-foreground text-xs font-medium uppercase tracking-wider">
              Sold Out
            </span>
          }
          {hasDiscount &&
          <span className="absolute top-3 right-3 px-2 py-1 bg-primary text-primary-foreground text-xs font-medium uppercase tracking-wider">
              Sale
            </span>
          }
        </div>
        </Link>




      <div className="p-4 flex flex-col flex-1">
        <Link to={`/product/${node.handle}`}>
          <h3 className="font-medium text-sm leading-tight line-clamp-2 group-hover:text-primary transition-colors">
            {node.title}
          </h3>
        </Link>

        {pair && (
          <div
            className="mt-3 inline-flex w-full rounded-full border border-border bg-background/60 p-0.5 text-[10px] font-medium uppercase tracking-wider"
            onClick={(e) => e.stopPropagation()}
          >
            {(['standard', 'zip'] as const).map((key) => {
              const isActive = variantKey === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setVariantKey(key);
                  }}
                  className={cn(
                    'flex-1 px-2 py-1.5 rounded-full transition-colors min-h-[32px]',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                  aria-pressed={isActive}
                >
                  {key === 'standard' ? 'Standard' : 'Premium Zip'}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="flex items-center gap-2">
            <span className="text-primary font-semibold">
              R{price.toFixed(2)}
            </span>
            {hasDiscount &&
            <span className="text-muted-foreground line-through text-sm">
                R{compareAtPrice.toFixed(2)}
              </span>
            }
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleWishlistClick}
              className={cn(
                'relative p-2 rounded-full transition-all duration-300',
                'bg-background/80 backdrop-blur-sm border border-border/50',
                'hover:bg-primary hover:border-primary group/heart',
                isHeartAnimating && 'scale-110'
              )}
              aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}>

              <Heart
                className={cn(
                  'w-4 h-4 transition-all duration-300',
                  inWishlist ?
                  'fill-primary text-primary group-hover/heart:fill-primary-foreground group-hover/heart:text-primary-foreground' :
                  'text-foreground group-hover/heart:text-primary-foreground',
                  isHeartAnimating && 'animate-pulse'
                )} />

            </button>
            <button
              onClick={handleAddToCart}
              disabled={isLoading || !node.availableForSale}
              className={cn(
                'relative p-2 rounded-full transition-all duration-300',
                'bg-background/80 backdrop-blur-sm border border-border/50',
                'hover:bg-primary hover:border-primary group/cart',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
              aria-label="Add to cart">

              <ShoppingCart className="w-4 h-4 text-foreground transition-all duration-300 group-hover/cart:text-primary-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>);

};
